import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * ### 外出外泊届け API 操作クラス
 */
export default class Outings {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET Outing
   */
  public async get(userStore: LoginUser, outingId: number): Promise<OutingGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      outingId: outingId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Outing, formatParams);

    const response = await this.commonService.executeGet<OutingGetModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GET Outings
   */
  public async gets(userStore: LoginUser, queryParams: { start: number; end: number; startIndex?: number; endIndex?: number }): Promise<Array<OutingGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Outings, formatParams);
    const response = await this.commonService.executeGet<Array<OutingGetModel>>(userStore, requestUrl, this.constructor.name, queryParams, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GET OutingsCount
   */
  public async getCount(userStore: LoginUser, queryParams: { start: number; end: number; startIndex?: number; endIndex?: number }): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Outings, formatParams);
    const response = await this.commonService.executeGet<{ count: number }>(
      userStore,
      `${requestUrl}/count`,
      this.constructor.name,
      queryParams,
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data.count;
  }

  /**
   * ### GET OutingNeedsMeals
   */
  public async getsNeedsMeals(userStore: LoginUser, queryParams: { start: number; end: number; startIndex?: number; endIndex?: number }): Promise<Array<OutingNeedsMealsGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OutingNeedsMeals, formatParams);
    const response = await this.commonService.executeGet<Array<OutingNeedsMealsGetModel>>(
      userStore,
      requestUrl,
      this.constructor.name,
      queryParams,
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data;
  }

  /**
   * ### GET OutingNeedsMealsCount
   */
  public async getNeedsMealsCount(userStore: LoginUser, queryParams: { start: number; end: number }): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OutingNeedsMeals, formatParams);
    const response = await this.commonService.executeGet<{ count: number }>(
      userStore,
      `${requestUrl}/count`,
      this.constructor.name,
      queryParams,
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data.count;
  }

  /**
   * ### GET AvailableStartDates
   */
  public async getAvailableStartDates(
    userStore: LoginUser,
    params: { type: string; residentId: number; year: number; month: number },
    queryParams?: { exclusionid: number }
  ): Promise<OutingAvailableDatesGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      type: params.type,
      residentId: params.residentId,
      year: params.year,
      month: params.month
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OutingAvailableStartDates, formatParams);
    const response = await this.commonService.executeGet<OutingAvailableDatesGetModel>(
      userStore,
      `${requestUrl}`,
      this.constructor.name,
      queryParams ? queryParams : {},
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data;
  }

  /**
   * ### GET AvailableEndDates
   */
  public async getAvailableEndDates(
    userStore: LoginUser,
    params: { type: string; residentId: number; datetime: string },
    queryParams?: { exclusionid: number }
  ): Promise<OutingAvailableDatesGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      type: params.type,
      residentId: params.residentId,
      datetime: params.datetime
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OutingAvailableEndDates, formatParams);
    const response = await this.commonService.executeGet<OutingAvailableDatesGetModel>(
      userStore,
      `${requestUrl}`,
      this.constructor.name,
      queryParams ? queryParams : {},
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data;
  }

  /**
   * ### POST Outing
   */
  public async post(userStore: LoginUser, requestBody: OutingPostModel): Promise<OutingGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Outings, formatParams);

    const response = await this.commonService.executePost<OutingGetModel>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * PATCH Outing
   */
  public async patch(userStore: LoginUser, outingId: number, requestBody: OutingPatchModel): Promise<OutingGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      outingId: outingId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Outing, formatParams);

    const response = await this.commonService.executePatch<OutingGetModel>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * PATCH OutingStatus
   */
  public async patchStatus(userStore: LoginUser, outingId: number, requestBody: OutingStatusPatchModel): Promise<OutingGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      outingId: outingId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OutingsStatus, formatParams);

    const response = await this.commonService.executePatch<OutingGetModel>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface OutingGetModel {
  /** 施設を特定するためのID */
  facilityId: number;
  /** 施設内で一意のID */
  id: number;

  /** 外出、外泊種別 */
  type: OutingType;
  /** 入力者 */
  inputBy: InputBy;
  /** 承諾 */
  consented: boolean;
  /** 承諾日時 */
  consentedDatetime: number | null;
  /** 承諾したユーザーID */
  consentedUserId: string | null;
  /** 外出外泊開始日時 */
  startDatetime: number;
  /** 外出外泊終了日時 */
  endDatetime: number;
  /**
   * 申請者家族を一意に特定するキー（residentsTBLのresidentsSortKey）
   * 代理予約で未登録家族を登録する場合はfamilyIdは0が格納されている
   */
  residentsSortKey: string;
  /** 外出外泊対象の利用者の申請時の情報 */
  targetResidentInfo: TargetResidentInfo;
  /** 申請した家族の申請時の情報 */
  applicantFamilyInfo: FamilyInfo;
  /** 主な外出場所（100文字） */
  location: string;
  /** お迎え。申請者本人（true）、申請者以外（false） */
  applicantPickup: boolean;
  /** お迎え（自由記述） */
  pickupPerson: FamilyInfo | null | undefined;
  /** 外出手段 */
  selectedVehicle: string | undefined;
  /** 外出外泊開始日の食事が必要かどうか */
  startDateNeedsMeal: NeedsMeal;
  /** 外出外泊終了日の食事が必要かどうか */
  endDateNeedsMeal: NeedsMeal | null | undefined;
  /** 同一利用者の家族に共有するか否か。v3.38.0以前はundefined */
  isSharedOuting: boolean;
  /** 連絡事項（1000文字） */
  text: string;

  /** 削除フラグ */
  deleteFlag: boolean;
  /** レコード作成日 */
  created: number;
  /** レコード更新日 */
  updated: number;
}

/** 外出、外泊種別 */
export enum OutingType {
  /** 外出届け */
  GOING_OUT = "goingOut",
  /** 外泊届け */
  STAYING_OUT = "stayingOut"
}

/** 入力者 */
export enum InputBy {
  /** 家族本人 */
  FAMILY = "family",
  /** 施設代理者 */
  FACILITY = "facility"
}

/** 外出外泊対象の利用者の申請時の情報 */
export interface TargetResidentInfo {
  /** 利用者名（32文字） */
  name: string;
  /** 利用者名（ふりがな） */
  kanaName: string;
  /** 部屋 ID */
  roomId: number;
  /** 部屋名 */
  roomName: string;
  /** 利用者グループID */
  groupId: number;
}

/** 申請した家族の申請時の情報 */
export interface FamilyInfo {
  /** 家族名（32文字） */
  name: string;
  /** 続柄（10文字） */
  relation: string;
  /** 電話番号（14文字） */
  tel: string;
}

/** 食事が必要かどうか */
export interface NeedsMeal {
  /** 朝食 */
  breakfast: boolean;
  /** 昼食 */
  lunch: boolean;
  /** おやつ */
  snack: boolean;
  /** 夕食 */
  dinner: boolean;
}

export interface OutingNeedsMealsGetModel {
  /** 日付（時間以降は0が格納される17桁） */
  datetime: number;
  /** 食事キャンセル情報 */
  needsMeals: Array<ExtNeedsMeal>;
}

export interface ExtNeedsMeal extends NeedsMeal {
  /** 外出外泊届けID */
  id: number;
  /** 利用者グループID */
  residentGroupId: number;
  /** 利用者名 */
  residentName: string;
  /** 部屋ID */
  roomId: number;
  /** 部屋名 */
  roomName: string;
}

/**
 * ## 届け出可能「年月日」（開始）
 *
 * - 2025年2月10日ならプロパティ名が"20250210"
 * - 届け出可能な「年月日」しかプロパティは存在しないため、"20250210"が存在しない場合がありえる
 * - 値は届け出可能な「時」を持つ
 *   - 「時」が0件（プロパティが存在しない）の場合はその「日」は無効として扱う
 *   - そのためAvailableHourにプロパティが存在しないことはありえない
 */
export interface OutingAvailableDatesGetModel {
  [key: string]: AvailableHour;
}

/**
 * ## 届け出可能「時」（開始）
 *
 * - 1時ならプロパティ名が"1"
 * - 届け出可能な「時」しかプロパティは存在しないため、1が存在しない場合がありえる
 * - 値は届け出可能な「分」を持つ
 *   - 「分」が0件の場合はその「時」は無効として扱う
 *   - そのため配列の要素が0件はありえない
 */
export interface AvailableHour {
  [key: string]: Array<string>;
}

export interface OutingPostModel {
  /** 外出、外泊種別 */
  type: OutingType;
  /** 入力者 */
  inputBy: InputBy;
  /** 外出外泊開始日時 */
  startDatetime: number;
  /** 外出外泊終了日時 */
  endDatetime: number;
  /** 申請者家族を一意に特定するキー（residentsTBLのresidentsSortKey） */
  residentsSortKey: string;
  /** 外出外泊対象の利用者の申請時の情報 */
  targetResidentInfo: TargetResidentInfo;
  /** 申請した家族の申請時の情報 */
  applicantFamilyInfo: FamilyInfo;
  /** 主な外出場所（100文字） */
  location: string;
  /** お迎え。申請者本人（true）、申請者以外（false） */
  applicantPickup: boolean;
  /** お迎え（自由記述） */
  pickupPerson: FamilyInfo | null | undefined;
  /** 外出手段 */
  selectedVehicle: string | undefined;
  /** 外出外泊開始日の食事が必要かどうか */
  startDateNeedsMeal: NeedsMeal;
  /** 外出外泊終了日の食事が必要かどうか */
  endDateNeedsMeal: NeedsMeal | null | undefined;
  /** 同一利用者の家族に共有するか否か。v3.38.0以前はundefined */
  isSharedOuting: boolean;
  /** 連絡事項（1000文字） */
  text: string;
}

export interface OutingPatchModel {
  /** 外出、外泊種別 */
  type: OutingType;
  /** 外出外泊開始日時 */
  startDatetime: number;
  /** 外出外泊終了日時 */
  endDatetime: number;
  /** 主な外出場所（100文字） */
  location: string;
  /** お迎え。申請者本人（true）、申請者以外（false） */
  applicantPickup: boolean;
  /** お迎え（自由記述） */
  pickupPerson: FamilyInfo | null | undefined;
  /** 外出手段 */
  selectedVehicle: string | undefined;
  /** 外出外泊開始日の食事が必要かどうか */
  startDateNeedsMeal: NeedsMeal;
  /** 外出外泊終了日の食事が必要かどうか */
  endDateNeedsMeal: NeedsMeal | null | undefined;
  /** 同一利用者の家族に共有するか否か。v3.38.0以前はundefined */
  isSharedOuting: boolean;
  /** 連絡事項（1000文字） */
  text: string;

  /** レコード更新日 */
  updated: number;
}

export interface OutingStatusPatchModel {
  /** 更新種別 */
  statusType: UpdateOutingStatusType;
  /** 送信メッセージ件名 */
  title?: string;
  /** 送信メッセージ本文 */
  text?: string;
  /** 整合性チェック用の更新日時 */
  updated: number;
}

/** 外出外泊届け 状態更新種別 */
export enum UpdateOutingStatusType {
  UNKNOWN = "",
  /** 承諾 */
  CONSENTED = "consented",
  /** 辞退 */
  REFUSE = "refuse",
  /** キャンセル */
  CANCEL = "cancel"
}
